import React, { useState } from "react";
import { Button } from "primereact/button";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { fetchUserBasicInfoFx } from "@/features/user/store";
import { Link } from "react-router-dom";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { setModalIsOpened } from "@/core/store/app";

interface PartnerData {
  id: string;
  title: string;
  logo_uri: string;
  partner_type: { id: string; name: string };
  country: { id: string; name: string };
  since: string;
  size: { id: string; name: string };
  pic_number: string;
  sectors: { id: string; name: string }[];
}

interface MemberViewProps {
  partnerData: PartnerData | null;
}

export const MemberView: React.FC<MemberViewProps> = ({ partnerData }) => {
  const [leaveOrgLoading, setLeaveOrgLoading] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const handleLeaveOrganization = async () => {
    setLeaveOrgLoading(true);
    try {
      await apiService.post(API_ENDPOINTS.PARTNERS.PARTNER_LEAVE, {});
      fetchUserBasicInfoFx();
      // console.log("Successfully left organization");
      setShowLeaveModal(false);
      setModalIsOpened(false);
    } catch (error) {
      console.error("Failed to leave organization:", error);
    } finally {
      setLeaveOrgLoading(false);
    }
  };

  const openLeaveModal = () => {
    setShowLeaveModal(true);
    setModalIsOpened(true);
  };

  const closeLeaveModal = () => {
    setShowLeaveModal(false);
    setModalIsOpened(false);
  };

  if (!partnerData) {
    return <div>No organization data available</div>;
  }

  return (
    <div className="member-organization-section">
      <div className="partner-container">
        <Link to={`/partner/${partnerData.id}`}>
          <div className="partner-header">
            <div className="header-left">
              <div className="partner-logo">
                {partnerData.logo_uri &&
                partnerData.logo_uri !==
                  "partner-images/logo_not_available.jpg" ? (
                  <img src={partnerData.logo_uri} alt={partnerData.title} />
                ) : (
                  <span>
                    {partnerData.title?.charAt(0)?.toUpperCase() || "P"}
                  </span>
                )}
              </div>
              <div className="partner-info">
                <h1>{partnerData.title}</h1>
                <div className="partner-meta">
                  <span className="meta-item">
                    <SvgIcon icon="icon-building" />
                    {partnerData.partner_type?.name}
                  </span>
                  <span className="meta-item">
                    <SvgIcon icon="icon-location" />
                    {partnerData.country?.name}
                  </span>
                  <span className="meta-item">
                    <SvgIcon icon="icon-calendar" />
                    {partnerData.since}
                  </span>
                  <span className="meta-item">
                    <SvgIcon icon="icon-users" />
                    {partnerData.size?.name}
                  </span>
                  {partnerData.pic_number && (
                    <span className="meta-item">
                      <SvgIcon icon="icon-hash" />
                      PIC Number {partnerData.pic_number}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {partnerData.sectors && partnerData.sectors.length > 0 && (
            <div className="partner-sectors">
              {partnerData.sectors.slice(0, 6).map((sector, index) => (
                <span key={index} className="sector-tag">
                  {sector.name}
                </span>
              ))}
              {partnerData.sectors.length > 6 && (
                <span className="tag more">
                  +{partnerData.sectors.length - 6}
                </span>
              )}
            </div>
          )}
        </Link>
      </div>

      <div className="leave-organization-section">
        <Button
          label="Leave Organization"
          severity="danger"
          outlined
          icon="pi pi-sign-out"
          className="leave-org-btn"
          onClick={openLeaveModal}
        />
      </div>

      <CommonModal
        isOpen={showLeaveModal}
        onClose={closeLeaveModal}
        width={400}
        height={280}
        icon="icon-warning"
        // iconVariant="danger"
        hasForm={false}
        actions={
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeLeaveModal}
              disabled={leaveOrgLoading}
            >
              Cancel
            </button>
            <Button
              type="button"
              className="btn-danger"
              severity="danger"
              onClick={handleLeaveOrganization}
              disabled={leaveOrgLoading}
            >
              {leaveOrgLoading ? "Leaving..." : "Leave"}
            </Button>
          </div>
        }
      >
        <div className="leave-modal-content">
          <div className="leave-modal-title">
            Are you sure you want to leave your organisation?
          </div>
          <div className="leave-modal-description">
            You will be removed as a member of the organisation.
          </div>
        </div>
      </CommonModal>
    </div>
  );
};
